import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Bienvenido a NomiDiem");
      nav("/dashboard");
    } else {
      setErr(r.error);
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F8FAFC]">
      {/* Left visual */}
      <div className="hidden md:flex relative overflow-hidden bg-[#0F6E56]">
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#1D9E75] flex items-center justify-center">
              <span className="font-bold">ND</span>
            </div>
            <div>
              <div className="text-lg font-bold tracking-tight">NomiDiem</div>
              <div className="text-[11px] uppercase tracking-[0.2em] opacity-70">Control Panel</div>
            </div>
          </div>
          <div className="space-y-4">
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              Adelantos de nómina <br/>con control total.
            </h1>
            <p className="text-sm text-white/80 max-w-md leading-relaxed">
              Tu salario devengado, disponible cuando lo necesitas. Gestión simple, segura y en tiempo real.
            </p>
            <div className="flex items-center gap-6 pt-4 text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#6EE7B7]" />
                Datos persistentes
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#6EE7B7]" />
                Acceso por rol
              </div>
            </div>
          </div>
          <div className="text-xs text-white/60 font-mono">
            v1.0 · Hecho en México
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="login-form" autoComplete="off">
          <div className="md:hidden mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-md bg-[#1D9E75] flex items-center justify-center text-white font-bold">ND</div>
              <div className="text-lg font-bold tracking-tight">NomiDiem</div>
            </div>
            <p className="text-sm text-[#64748B] mt-3 leading-relaxed">
              Tu salario devengado, disponible cuando lo necesitas. Gestión simple, segura y en tiempo real.
            </p>
          </div>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-[#0F172A]">Iniciar sesión</h2>
            <p className="text-sm text-[#64748B] mt-1.5">Ingresa tus credenciales para continuar.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#475569]">Correo electrónico</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#475569]">Contraseña</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                className="h-11"
              />
            </div>
          </div>

          {err && (
            <div data-testid="login-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full h-11 bg-[#1D9E75] hover:bg-[#147D5C] text-white font-semibold"
          >
            {loading ? "Ingresando..." : "Ingresar"}
          </Button>

          <div className="pt-4 border-t border-[#E2E8F0] text-center text-xs text-[#64748B] space-y-1" data-testid="login-footer">
            <div>
              <a href="https://nomidiem.app" className="text-[#147D5C] hover:underline font-medium">nomidiem.app</a>
              <span className="mx-1.5 text-[#CBD5E1]">·</span>
              <a href="mailto:hola@nomidiem.com" className="text-[#147D5C] hover:underline font-medium">hola@nomidiem.com</a>
            </div>
            <div className="text-[11px] text-[#94A3B8]">© {new Date().getFullYear()} NomiDiem · Tu nómina, cada día.</div>
          </div>
        </form>
      </div>
    </div>
  );
}
